import { useEffect, useRef, useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_RETRIES = 5;

export function useSSE(token) {
  const [connected, setConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState(null);
  const esRef = useRef(null);
  const retriesRef = useRef(0);
  const timerRef = useRef(null);
  const unmountedRef = useRef(false);

  useEffect(() => {
    if (!token) return;
    unmountedRef.current = false;

    function connect() {
      if (unmountedRef.current) return;

      const es = new EventSource(`${API}/stream?token=${encodeURIComponent(token)}`);
      esRef.current = es;

      es.onopen = () => {
        if (unmountedRef.current) { es.close(); return; }
        setConnected(true);
        retriesRef.current = 0;
      };

      es.onmessage = (e) => {
        if (unmountedRef.current) return;
        if (!e.data || !e.data.trim()) return;
        try {
          const parsed = JSON.parse(e.data);
          setLastEvent(parsed);
        } catch {
          // Ignorer ugyldigt JSON
        }
      };

      es.onerror = () => {
        if (unmountedRef.current) { es.close(); return; }
        es.close();
        setConnected(false);

        if (retriesRef.current >= MAX_RETRIES) return;

        const delay = Math.min(1000 * 2 ** retriesRef.current, 30000);
        retriesRef.current += 1;
        timerRef.current = setTimeout(connect, delay);
      };
    }

    connect();

    return () => {
      unmountedRef.current = true;
      clearTimeout(timerRef.current);
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
      }
      setConnected(false);
    };
  }, [token]);

  return { connected, lastEvent };
}
